import { FC, useEffect, useRef, useState } from 'react'
import { ReactComponent as FilterIcon } from './filter.svg'
import { ReactComponent as SortIcon } from './setting-4.svg'
import { FiltersModal } from './FiltersModal'
import classes from './filtersBar.module.scss'
import { AdFilters } from '../index'

const SORT_OPTIONS = ['Most recent', 'Impressions: high to low']

interface FiltersBarProps {
  filters: AdFilters
  onFiltersChange: (filters: AdFilters) => void
}

export const FiltersBar: FC<FiltersBarProps> = ({
  filters,
  onFiltersChange,
}) => {
  const [search, setSearch] = useState('')
  const [sortOpen, setSortOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectedSort, setSelectedSort] = useState('Most recent')
  const sortRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <>
      <div className={classes.bar}>
        <div className={classes.searchWrapper}>
          <svg
            fill="none"
            height="16"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
              stroke="#25262D"
              strokeOpacity="0.4"
              strokeWidth="1.5"
            />
            <path
              d="M21 21l-4.35-4.35"
              stroke="#25262D"
              strokeLinecap="round"
              strokeOpacity="0.4"
              strokeWidth="1.5"
            />
          </svg>
          <input
            className={classes.searchInput}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by keyword"
            type="text"
            value={search}
          />
        </div>

        {filters.activeStatus !== 'Active and inactive' && (
          <button
            className={classes.activeAdsBtn}
            onClick={() =>
              onFiltersChange({
                ...filters,
                activeStatus: 'Active and inactive',
              })
            }
            type="button"
          >
            <span className={classes.activeAdsClear}>×</span>
            {filters.activeStatus}
          </button>
        )}

        <button
          className={classes.filterBtn}
          onClick={() => setFiltersOpen(true)}
          type="button"
        >
          <FilterIcon />
          Filters
        </button>

        <div className={classes.sortWrapper} ref={sortRef}>
          <button
            className={`${classes.sortBtn} ${sortOpen ? classes.sortBtnActive : ''}`}
            onClick={() => setSortOpen((v) => !v)}
            type="button"
          >
            <SortIcon />
            Sort by
            <svg
              fill="none"
              height="14"
              viewBox="0 0 24 24"
              width="14"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={sortOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeOpacity="0.7"
                strokeWidth="1.5"
              />
            </svg>
          </button>

          {sortOpen && (
            <div className={classes.sortDropdown}>
              {SORT_OPTIONS.map((option) => {
                const isSelected = selectedSort === option
                return (
                  <button
                    className={`${classes.sortOption} ${isSelected ? classes.sortOptionSelected : ''}`}
                    key={option}
                    onClick={() => {
                      setSelectedSort(option)
                      setSortOpen(false)
                    }}
                    type="button"
                  >
                    {isSelected ? (
                      <div className={classes.doubleTick}>
                        <svg
                          fill="none"
                          height="10"
                          viewBox="0 0 24 24"
                          width="10"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 12l5 5L12 7M9 12l5 5 7-9"
                            stroke="#ffffff"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                          />
                        </svg>
                      </div>
                    ) : (
                      <span className={classes.sortOptionCircle} />
                    )}
                    {option}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <FiltersModal
          filters={filters}
          onClose={() => setFiltersOpen(false)}
          onFiltersChange={onFiltersChange}
        />
      )}
    </>
  )
}
